import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  ChevronRight,
  Gift,
  Calendar,
  Users,
  ChevronLeft,
  Plus,
  Power,
  PowerOff,
  ShieldCheck,
  Copy,
  Check,
} from "lucide-react";

// Public-facing referral link host. Permanent production URL — independent of
// preview env so admins can copy a link they can hand out anywhere.
const PUBLIC_REFERRAL_HOST = "https://quickinbox.app";
function buildReferralLink(code) {
  return code ? `${PUBLIC_REFERRAL_HOST}/r/${code}` : "";
}
async function copyToClipboard(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const EVENT_TYPES = ["referral_activated", "signup", "pro_purchase"];
const REWARD_TYPES = ["days", "none", "alias", "pro_trial"];

/**
 * Admin Referrals page.
 *
 * Two tabs:
 *  - Overview: existing referral-graph table with click-through detail.
 *  - Reward Policies: admin-managed referral reward policies (Active card,
 *    history table, create-policy dialog, activate/deactivate actions).
 */
export default function AdminReferralsPage() {
  const [tab, setTab] = useState("overview");

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-7xl" data-testid="admin-referrals-page">
      <div className="flex items-center gap-2">
        <Gift size={18} className="text-[var(--color-l-accent,#CC0000)]" />
        <h1 className="text-xl font-semibold">Referrals</h1>
      </div>

      {/* Custom segmented tab bar — high contrast, full-width on mobile, clearly clickable. */}
      <div
        role="tablist"
        aria-label="Referrals sections"
        className="inline-flex w-full sm:w-auto rounded-lg border border-[#E5E5E5] bg-white p-1 shadow-sm gap-1"
        data-testid="admin-referrals-tabs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === "overview"}
          onClick={() => setTab("overview")}
          className={`flex-1 sm:flex-none sm:min-w-[140px] px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${
            tab === "overview"
              ? "bg-[#111111] text-white shadow-sm"
              : "text-[#444444] hover:bg-[#F5F5F5]"
          }`}
          data-testid="admin-referrals-tab-overview"
        >
          <Users size={14} /> Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "policies"}
          onClick={() => setTab("policies")}
          className={`flex-1 sm:flex-none sm:min-w-[180px] px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${
            tab === "policies"
              ? "bg-[#111111] text-white shadow-sm"
              : "text-[#444444] hover:bg-[#F5F5F5]"
          }`}
          data-testid="admin-referrals-tab-policies"
        >
          <ShieldCheck size={14} /> Reward Policies
        </button>
      </div>

      <div className="mt-4">
        {tab === "overview" ? <OverviewTab /> : <PoliciesTab />}
      </div>
    </div>
  );
}

// ─── Overview Tab (existing list + detail) ─────────────────────────────
function OverviewTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [me, setMe] = useState(null); // current admin's own referral code/link

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/referrals");
        setRows(r.data?.items || []);
      } finally { setLoading(false); }
    })();
    // Fetch the admin's own referral snapshot. Lazily generates a code if
    // the admin doesn't have one yet — exposes a copyable link immediately.
    (async () => {
      try {
        const r = await api.get("/referral/me");
        setMe(r.data || null);
      } catch {
        setMe(null);
      }
    })();
  }, []);

  const openDetail = async (tenantId) => {
    setSelected(tenantId);
    setDetailLoading(true);
    try {
      const r = await api.get(`/admin/referrals/${encodeURIComponent(tenantId)}`);
      setDetail(r.data);
    } catch {
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-zinc-500 flex items-center gap-2" data-testid="admin-referrals-loading">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </div>
    );
  }

  if (selected) {
    return (
      <div className="space-y-4" data-testid="admin-referral-detail">
        <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setDetail(null); }} className="gap-1" data-testid="admin-referral-detail-back">
          <ChevronLeft size={14} /> Back to list
        </Button>
        {detailLoading || !detail ? (
          <div className="text-sm text-zinc-500 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : <DetailView detail={detail} />}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <MyReferralCard me={me} />

      <div className="text-xs text-zinc-500 font-mono" data-testid="admin-referrals-count">
        {rows.length} account{rows.length !== 1 ? "s" : ""}
      </div>

      <div className="border border-[#E5E5E5] rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[11px] uppercase tracking-wider text-[#888888]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">User</th>
                <th className="text-left px-3 py-2 font-semibold">Plan</th>
                <th className="text-right px-3 py-2 font-semibold">Base expiry</th>
                <th className="text-right px-3 py-2 font-semibold">Effective expiry</th>
                <th className="text-right px-3 py-2 font-semibold">Reward days</th>
                <th className="text-right px-3 py-2 font-semibold">Referrals</th>
                <th className="text-right px-3 py-2 font-semibold">Activated</th>
                <th className="text-right px-3 py-2 font-semibold">Last activity</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F0F0]">
              {rows.length === 0 && (
                <tr><td colSpan={9} className="text-center text-xs text-zinc-500 py-8" data-testid="admin-referrals-empty">No referral activity yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.tenant_id} className="hover:bg-[#FAFAFA] cursor-pointer" onClick={() => openDetail(r.tenant_id)} data-testid={`admin-referrals-row-${r.tenant_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-[#111111]" data-testid={`admin-referrals-row-label-${r.tenant_id}`}>{r.label}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{r.referral_code || "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{r.plan_type || "—"}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono">{fmtDate(r.base_expires_at)}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono">{fmtDate(r.effective_expires_at)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{r.reward_days_total || 0}</td>
                  <td className="px-3 py-2 text-right">{r.total_referrals || 0}</td>
                  <td className="px-3 py-2 text-right">{r.activated_referrals || 0}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono">{fmtDate(r.last_activity_at)}</td>
                  <td className="px-3 py-2 text-right">
                    {r.referral_code ? (
                      <RowCopyButton code={r.referral_code} />
                    ) : (
                      <ChevronRight size={14} className="text-zinc-400 inline" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── My referral card — code + link + copy buttons ─────────────────────
function MyReferralCard({ me }) {
  const [copied, setCopied] = useState("");

  const code = me?.referral_code || "";
  const link = buildReferralLink(code);

  const doCopy = async (kind, value) => {
    if (!value) return;
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(kind);
      toast.success(kind === "link" ? "Invite link copied" : "Code copied");
      setTimeout(() => setCopied(""), 1500);
    } else {
      toast.error("Could not copy to clipboard");
    }
  };

  return (
    <div
      className="rounded-lg border border-[#E5E5E5] bg-white p-4 sm:p-5 shadow-sm"
      data-testid="admin-my-referral-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2">
          <Gift size={14} className="text-[var(--color-l-accent,#CC0000)]" />
          <h2 className="text-sm font-semibold text-[#111111]">My referral link</h2>
        </div>
        {me && (
          <div className="text-[11px] text-zinc-500 font-mono">
            {me.total_invites || 0} invites · {me.activated_invites || 0} activated · {me.reward_days_total || 0} days earned
          </div>
        )}
      </div>

      {!code ? (
        <p className="mt-3 text-xs text-zinc-500" data-testid="admin-my-referral-empty">
          Generating your referral code… open this page again in a moment.
        </p>
      ) : (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <code
            className="flex-1 text-xs font-mono bg-[#F5F5F5] border border-[#E5E5E5] rounded-md px-3 py-2 truncate text-[#333333]"
            data-testid="admin-my-referral-link"
            title={link}
          >
            {link}
          </code>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-9 gap-1 text-xs"
              onClick={() => doCopy("link", link)}
              data-testid="admin-my-referral-copy-link"
            >
              {copied === "link" ? <Check size={13} /> : <Copy size={13} />} Copy link
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-9 gap-1 text-xs"
              onClick={() => doCopy("code", code)}
              data-testid="admin-my-referral-copy-code"
            >
              {copied === "code" ? <Check size={13} /> : <Copy size={13} />}
              <span className="font-mono">{code}</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Compact per-row "Copy link" button used in the referrals table.
function RowCopyButton({ code }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        const ok = await copyToClipboard(buildReferralLink(code));
        if (ok) {
          setCopied(true);
          toast.success("Invite link copied");
          setTimeout(() => setCopied(false), 1500);
        } else {
          toast.error("Could not copy to clipboard");
        }
      }}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-[#555555] hover:bg-[#F5F5F5] border border-[#E5E5E5]"
      data-testid={`admin-referrals-row-copy-${code}`}
      title={`Copy ${buildReferralLink(code)}`}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />} Copy
    </button>
  );
}


function DetailView({ detail }) {
  return (
    <div className="space-y-4">
      <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-2">
        <h2 className="text-base font-semibold flex items-center gap-2">
          {detail.label}
          <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 bg-[#F5F5F5] rounded text-[#555555]">{detail.kind}</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <Field label="Code" value={detail.referral_code || "—"} />
          <Field label="Referred by" value={detail.referred_by_user?.email || detail.referred_by_user?.label || "—"} />
          <Field label="Base expiry" value={fmtDate(detail.base_expires_at)} />
          <Field label="Effective expiry" value={fmtDate(detail.effective_expires_at)} />
          <Field label="Reward days" value={detail.reward_days_total || 0} />
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg p-4">
        <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-3"><Users size={14}/> Invitees ({detail.invited?.length || 0})</h3>
        {(detail.invited || []).length === 0 ? (
          <div className="text-xs text-zinc-500" data-testid="admin-referral-detail-empty-invitees">No invitees yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-[#888888]">
              <tr>
                <th className="text-left py-1">User</th>
                <th className="text-left py-1">Status</th>
                <th className="text-left py-1">Trigger</th>
                <th className="text-left py-1">Created</th>
                <th className="text-left py-1">Activated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F0F0]">
              {detail.invited.map((inv) => (
                <tr key={inv.referral_id} data-testid={`admin-referral-invitee-${inv.referral_id}`}>
                  <td className="py-1.5">{inv.referred_user?.email || inv.referred_user?.label || "Unknown user"}</td>
                  <td className="py-1.5"><StatusPill status={inv.status} /></td>
                  <td className="py-1.5">{inv.trigger_event}</td>
                  <td className="py-1.5 font-mono">{fmtDate(inv.created_at)}</td>
                  <td className="py-1.5 font-mono">{fmtDate(inv.activated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg p-4">
        <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-3"><Calendar size={14}/> Reward ledger ({detail.ledger?.length || 0})</h3>
        {(detail.ledger || []).length === 0 ? (
          <div className="text-xs text-zinc-500">No ledger entries yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-[#888888]">
              <tr>
                <th className="text-left py-1">Days</th>
                <th className="text-left py-1">Reason</th>
                <th className="text-left py-1">Status</th>
                <th className="text-left py-1">Applied</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F0F0]">
              {detail.ledger.map((e) => (
                <tr key={e.ledger_id} data-testid={`admin-referral-ledger-${e.ledger_id}`}>
                  <td className="py-1.5 font-semibold">+{e.reward_value_days}</td>
                  <td className="py-1.5">{e.reason}</td>
                  <td className="py-1.5"><StatusPill status={e.status} /></td>
                  <td className="py-1.5 font-mono">{fmtDate(e.applied_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#888888]">{label}</div>
      <div className="font-mono text-[#111111] truncate">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    rewarded: "bg-emerald-50 text-emerald-700",
    activated: "bg-blue-50 text-blue-700",
    pending: "bg-amber-50 text-amber-700",
    rejected: "bg-red-50 text-red-700",
    applied: "bg-emerald-50 text-emerald-700",
    cancelled: "bg-zinc-100 text-zinc-600",
  };
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${map[status] || "bg-zinc-100 text-zinc-700"}`}>{status}</span>;
}

// ─── Reward Policies Tab ───────────────────────────────────────────────
function PoliciesTab() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activateTarget, setActivateTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/referrals/policies");
      const list = r.data?.items || [];
      setItems(list);
      // Derive active from items so any active policy (regardless of event_type)
      // is reflected in the warning state. Backend `active` field only checks
      // `referral_activated`, which falsely triggers the empty warning when a
      // signup/pro_purchase policy is the active one.
      const fromItems = list.find((p) => p.is_active) || null;
      setActive(r.data?.active || fromItems);
    } catch (e) {
      toast.error("Failed to load policies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCreated = () => {
    setShowCreate(false);
    toast.success("Policy created as draft. Click Activate to enable it.");
    load();
  };

  const doActivate = async (policy) => {
    setBusyId(policy.policy_id);
    try {
      const r = await api.post(`/admin/referrals/policies/${policy.policy_id}/activate`);
      const replaced = r.data?.deactivated || [];
      toast.success(
        replaced.length
          ? `Activated. Deactivated ${replaced.length} previous policy.`
          : "Policy activated."
      );
      setActivateTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed");
    } finally {
      setBusyId(null);
    }
  };

  const doDeactivate = async (policy) => {
    setBusyId(policy.policy_id);
    try {
      await api.post(`/admin/referrals/policies/${policy.policy_id}/deactivate`);
      toast.success("Policy deactivated.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deactivation failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-policies-tab">
      {/* Header row: count + Create button (mobile-stacked) */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="text-xs text-zinc-500 font-mono" data-testid="admin-policies-count">
          {items.length} polic{items.length !== 1 ? "ies" : "y"}
        </div>
        <div className="sm:ml-auto">
          <Button
            size="sm"
            onClick={() => setShowCreate(true)}
            className="gap-1 w-full sm:w-auto"
            data-testid="admin-policies-create-btn"
          >
            <Plus size={14} /> Create policy
          </Button>
        </div>
      </div>

      {/* Active policy card (highly visible) */}
      <ActivePolicyCard policy={active} loading={loading} />

      {/* Past / all policies table */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b border-[#F0F0F0] bg-[#FAFAFA] flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#666666]">All policies</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-xs text-zinc-500" data-testid="admin-policies-empty">
            No policies yet. Click <span className="font-semibold">Create policy</span> to add one.
          </div>
        ) : (
          <>
            {/* Desktop / Tablet table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white text-[11px] uppercase tracking-wider text-[#888888]">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Name</th>
                    <th className="text-left px-3 py-2 font-semibold">Event</th>
                    <th className="text-left px-3 py-2 font-semibold">Referrer</th>
                    <th className="text-left px-3 py-2 font-semibold">Invitee</th>
                    <th className="text-left px-3 py-2 font-semibold">Status</th>
                    <th className="text-left px-3 py-2 font-semibold">Window</th>
                    <th className="text-right px-3 py-2 font-semibold">Updated</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0F0F0]">
                  {items.map((p) => (
                    <tr key={p.policy_id} data-testid={`admin-policy-row-${p.policy_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-[#111111]">{p.name}</div>
                        <div className="text-[10px] text-zinc-400 font-mono">v{p.version}</div>
                      </td>
                      <td className="px-3 py-2 text-xs font-mono">{p.event_type}</td>
                      <td className="px-3 py-2 text-xs">{rewardLabel(p.referrer_reward_type, p.referrer_reward_value)}</td>
                      <td className="px-3 py-2 text-xs">{rewardLabel(p.invitee_reward_type, p.invitee_reward_value)}</td>
                      <td className="px-3 py-2">
                        {p.is_active
                          ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700" data-testid={`admin-policy-active-pill-${p.policy_id}`}>ACTIVE</span>
                          : <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">draft</span>}
                      </td>
                      <td className="px-3 py-2 text-[11px] font-mono text-zinc-600">
                        {p.starts_at ? fmtDate(p.starts_at) : "—"} → {p.ends_at ? fmtDate(p.ends_at) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-[11px] font-mono">{fmtDate(p.updated_at)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {p.is_active ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 h-7 text-xs"
                            onClick={() => doDeactivate(p)}
                            disabled={busyId === p.policy_id}
                            data-testid={`admin-policy-deactivate-btn-${p.policy_id}`}
                          >
                            {busyId === p.policy_id ? <Loader2 size={12} className="animate-spin" /> : <PowerOff size={12} />}
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="gap-1 h-7 text-xs"
                            onClick={() => setActivateTarget(p)}
                            disabled={busyId === p.policy_id}
                            data-testid={`admin-policy-activate-btn-${p.policy_id}`}
                          >
                            {busyId === p.policy_id ? <Loader2 size={12} className="animate-spin" /> : <Power size={12} />}
                            Activate
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-[#F0F0F0]">
              {items.map((p) => (
                <div key={p.policy_id} className="p-4 space-y-2" data-testid={`admin-policy-card-${p.policy_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-[#111111]">{p.name}</div>
                      <div className="text-[10px] text-zinc-500 font-mono">{p.event_type} · v{p.version}</div>
                    </div>
                    {p.is_active
                      ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">ACTIVE</span>
                      : <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">draft</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-[10px] uppercase text-[#888888]">Referrer</div>
                      <div>{rewardLabel(p.referrer_reward_type, p.referrer_reward_value)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-[#888888]">Invitee</div>
                      <div>{rewardLabel(p.invitee_reward_type, p.invitee_reward_value)}</div>
                    </div>
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500">
                    Updated {fmtDate(p.updated_at)}
                  </div>
                  <div>
                    {p.is_active ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 w-full"
                        onClick={() => doDeactivate(p)}
                        disabled={busyId === p.policy_id}
                      >
                        {busyId === p.policy_id ? <Loader2 size={12} className="animate-spin" /> : <PowerOff size={12} />}
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="gap-1 w-full"
                        onClick={() => setActivateTarget(p)}
                        disabled={busyId === p.policy_id}
                      >
                        {busyId === p.policy_id ? <Loader2 size={12} className="animate-spin" /> : <Power size={12} />}
                        Activate
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Create dialog */}
      <CreatePolicyDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onCreated={onCreated}
      />

      {/* Activate confirmation */}
      <AlertDialog open={!!activateTarget} onOpenChange={(o) => !o && setActivateTarget(null)}>
        <AlertDialogContent data-testid="admin-policy-activate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Activate this policy?</AlertDialogTitle>
            <AlertDialogDescription>
              {activateTarget && active && active.event_type === activateTarget.event_type && active.policy_id !== activateTarget.policy_id ? (
                <>
                  The currently active policy <span className="font-semibold">{active.name}</span>
                  {" "}for event <span className="font-mono">{active.event_type}</span> will be deactivated.
                  Future referral rewards will follow{" "}
                  <span className="font-semibold">{activateTarget?.name}</span>.
                </>
              ) : (
                <>
                  This will start applying <span className="font-semibold">{activateTarget?.name}</span>{" "}
                  for event <span className="font-mono">{activateTarget?.event_type}</span> immediately.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-policy-activate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => activateTarget && doActivate(activateTarget)}
              data-testid="admin-policy-activate-confirm-btn"
            >
              Activate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function rewardLabel(type, value) {
  if (!type || type === "none") return "—";
  if (type === "days") return `+${value || 0} day${(value || 0) === 1 ? "" : "s"}`;
  return `${type}:${value}`;
}

function ActivePolicyCard({ policy, loading }) {
  if (loading) {
    return (
      <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 text-sm text-zinc-500 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading active policy…
      </div>
    );
  }
  if (!policy) {
    return (
      <div
        className="rounded-lg p-4 border-2 border-dashed border-amber-300 bg-amber-50/60"
        data-testid="admin-policy-active-card-empty"
      >
        <div className="flex items-center gap-2 text-amber-800">
          <ShieldCheck size={16} />
          <div className="text-sm font-semibold">No active reward policy</div>
        </div>
        <div className="mt-1 text-xs text-amber-900/80">
          Referrals will not award any reward until a policy is activated.
        </div>
      </div>
    );
  }
  return (
    <div
      className="rounded-lg p-4 sm:p-5 border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white shadow-sm"
      data-testid="admin-policy-active-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-600 text-white tracking-wider">ACTIVE</span>
          <div className="text-base font-semibold text-[#111111]" data-testid="admin-policy-active-name">{policy.name}</div>
        </div>
        <div className="text-[11px] font-mono text-zinc-500">
          v{policy.version} · updated {fmtDate(policy.updated_at)}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <Field label="Event" value={policy.event_type} />
        <Field label="Referrer" value={rewardLabel(policy.referrer_reward_type, policy.referrer_reward_value)} />
        <Field label="Invitee" value={rewardLabel(policy.invitee_reward_type, policy.invitee_reward_value)} />
        <Field label="Window" value={`${policy.starts_at ? fmtDate(policy.starts_at) : "always"} → ${policy.ends_at ? fmtDate(policy.ends_at) : "no end"}`} />
      </div>
    </div>
  );
}

function CreatePolicyDialog({ open, onOpenChange, onCreated }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm());

  function initialForm() {
    return {
      name: "",
      event_type: "referral_activated",
      referrer_reward_type: "days",
      referrer_reward_value: 1,
      invitee_reward_type: "days",
      invitee_reward_value: 1,
      starts_at: "",
      ends_at: "",
      notes: "",
    };
  }

  useEffect(() => {
    if (open) setForm(initialForm());
  }, [open]);

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        event_type: form.event_type,
        referrer_reward_type: form.referrer_reward_type,
        referrer_reward_value: form.referrer_reward_type === "none" ? 0 : Number(form.referrer_reward_value || 0),
        invitee_reward_type: form.invitee_reward_type,
        invitee_reward_value: form.invitee_reward_type === "none" ? 0 : Number(form.invitee_reward_value || 0),
        is_active: false, // always created as draft
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        notes: form.notes || null,
      };
      await api.post("/admin/referrals/policies", payload);
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-policy-create-dialog">
        <DialogHeader>
          <DialogTitle>Create reward policy</DialogTitle>
          <DialogDescription>
            New policies are saved as <span className="font-semibold">draft</span>. They take effect only after you click <span className="font-semibold">Activate</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="pol-name">Name</Label>
            <Input
              id="pol-name"
              placeholder="e.g. Holiday 2026 — 2 day boost"
              value={form.name}
              onChange={set("name")}
              data-testid="admin-policy-form-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pol-event">Event type</Label>
            <select
              id="pol-event"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              value={form.event_type}
              onChange={set("event_type")}
              data-testid="admin-policy-form-event"
            >
              {EVENT_TYPES.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>

          <RewardSideFields
            label="Referrer reward"
            typeKey="referrer_reward_type"
            valueKey="referrer_reward_value"
            form={form}
            setForm={setForm}
            testidPrefix="admin-policy-form-referrer"
          />

          <RewardSideFields
            label="Invitee reward"
            typeKey="invitee_reward_type"
            valueKey="invitee_reward_value"
            form={form}
            setForm={setForm}
            testidPrefix="admin-policy-form-invitee"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="pol-start">Start (optional)</Label>
              <Input
                id="pol-start"
                type="datetime-local"
                value={form.starts_at}
                onChange={set("starts_at")}
                data-testid="admin-policy-form-starts"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pol-end">End (optional)</Label>
              <Input
                id="pol-end"
                type="datetime-local"
                value={form.ends_at}
                onChange={set("ends_at")}
                data-testid="admin-policy-form-ends"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pol-notes">Internal notes (optional)</Label>
            <Textarea
              id="pol-notes"
              rows={2}
              placeholder="Why this policy exists, campaign reference, etc."
              value={form.notes}
              onChange={set("notes")}
              data-testid="admin-policy-form-notes"
            />
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="admin-policy-form-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting} className="gap-1" data-testid="admin-policy-form-submit">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Save as draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RewardSideFields({ label, typeKey, valueKey, form, setForm, testidPrefix }) {
  const type = form[typeKey];
  const isNone = type === "none";
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="grid grid-cols-3 gap-2">
        <select
          className="col-span-2 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          value={type}
          onChange={(e) => setForm((f) => ({ ...f, [typeKey]: e.target.value, [valueKey]: e.target.value === "none" ? 0 : f[valueKey] }))}
          data-testid={`${testidPrefix}-type`}
        >
          {REWARD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <Input
          type="number"
          min="0"
          value={isNone ? 0 : form[valueKey]}
          disabled={isNone}
          onChange={(e) => setForm((f) => ({ ...f, [valueKey]: e.target.value }))}
          data-testid={`${testidPrefix}-value`}
        />
      </div>
      {type !== "days" && type !== "none" && (
        <div className="text-[11px] text-amber-700">
          Note: <span className="font-mono">{type}</span> rewards are recorded but not auto-applied yet.
        </div>
      )}
    </div>
  );
}
